import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  faCircleQuestion,
  faClose,
  faGift,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FOOTBALL } from '@sorare/core/src/constants/__generated__/routes';
import { useSafePreviousNavigate } from '@sorare/core/src/hooks/useSafePreviousNavigate';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FAQDrawer } from '@sorare/marketplace/src/components/loan/FAQDrawer';

import { PrizePoolDialog } from '../PrizePoolDialog';
import type { LoanHeader_so5Leaderboard } from './__generated__/index.graphql';

const Header = styled.div`
  padding: var(--double-unit);
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  place-items: center;
  gap: var(--double-unit);
  text-align: center;

  @media ${tabletAndAbove} {
    place-items: center start;
    text-align: left;
  }
`;

const Right = styled(Horizontal)`
  place-self: end;
`;

export const LoanHeader = ({
  so5Leaderboard,
  eligibleUntil,
}: {
  eligibleUntil?: Date;
  so5Leaderboard: LoanHeader_so5Leaderboard;
}) => {
  const { message } = useTimeLeft(eligibleUntil);
  const goBack = useSafePreviousNavigate(FOOTBALL);
  const [showCompetitionRewards, setShowCompetitionRewards] =
    useState<boolean>(false);
  const [showFAQ, setShowFAQ] = useState<boolean>(false);
  return (
    <Header>
      <IconButton
        size="medium"
        color="quaternary"
        icon={faClose}
        onClick={goBack}
      />
      <Vertical gap={0}>
        <LabelM bold>
          <FormattedMessage
            id="loan.WelcomeScreen.title"
            defaultMessage="Welcome gift"
          />
        </LabelM>
        {!!eligibleUntil && (
          <LabelM color="var(--c-nd-600-opaque)">
            <FormattedMessage
              id="loan.WelcomeScreen.header.expire"
              defaultMessage="Expire in {value}"
              values={{ value: message }}
            />
          </LabelM>
        )}
      </Vertical>
      <Right gap={1}>
        <IconButton
          size="small"
          color="quaternary"
          icon={faGift}
          onClick={() => {
            setShowCompetitionRewards(true);
          }}
        />
        <IconButton
          size="small"
          color="quaternary"
          icon={faCircleQuestion}
          onClick={() => {
            setShowFAQ(true);
          }}
        />
      </Right>
      {!!showCompetitionRewards && !!so5Leaderboard && (
        <PrizePoolDialog
          so5Leaderboard={so5Leaderboard}
          onClose={() => setShowCompetitionRewards(false)}
        />
      )}
      <FAQDrawer open={showFAQ} onClose={() => setShowFAQ(false)} />
    </Header>
  );
};

LoanHeader.fragments = {
  so5Leaderboard: gql`
    fragment LoanHeader_so5Leaderboard on So5Leaderboard {
      slug
      ...PrizePoolDialog_so5Leaderboard
    }
    ${PrizePoolDialog.fragments.so5Leaderboard}
  ` as TypedDocumentNode<LoanHeader_so5Leaderboard>,
};
